import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import DropdownMenu from 'ext-ui/components/Dropdown/components/DropdownMenu/DropdownMenu'
import DropdownOption, {
  type DropdownOptionModel,
} from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption'
import { useDropdownLogic } from 'ext-ui/components/Dropdown/hooks/useDropdownLogic'
import React, { useEffect, useMemo, useRef } from 'react'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import SvgSidenavPersonalInfoInactive from 'src/components/Svgs/SvgSidenavPersonalInfoInactive'
import { useTranslate } from 'src/localization/Trns'
import { type Vendor, VendorStatus } from 'src/queries/vendors/vendors.types'

import style from './PaymentHistoryVendorDropdown.style'

interface PaymentHistoryVendorDropdownProps {
  vendors: Vendor[]
  allVendorsLoaded: boolean
  selectedVendorIds: string[] | null
  onSelect: (vendorIds: string[] | null) => void
}

const ALL_VENDORS_VALUE = '__all_vendors__'

const PaymentHistoryVendorDropdown: React.FC<
  PaymentHistoryVendorDropdownProps
> = ({ vendors, allVendorsLoaded, selectedVendorIds, onSelect }) => {
  const translate = useTranslate()
  const previousOpenRef = useRef(false)
  const allVendorsSelected =
    selectedVendorIds === null ||
    (allVendorsLoaded &&
      vendors.length > 0 &&
      vendors.every((vendor) => selectedVendorIds.includes(vendor.id)))
  const options = useMemo<DropdownOptionModel<Vendor | undefined>[]>(
    () => [
      { value: ALL_VENDORS_VALUE, label: translate('All Vendors') },
      ...vendors.map((vendor) => ({
        value: vendor.id,
        label: vendor.name,
        details: vendor,
        pill:
          vendor.status === VendorStatus.Archived
            ? translate('Archived')
            : undefined,
        disabled: selectedVendorIds === null && !allVendorsLoaded,
      })),
    ],
    [allVendorsLoaded, selectedVendorIds, translate, vendors],
  )
  const selectedValues = useMemo(() => {
    if (allVendorsSelected) return options
    return options.filter((option) => selectedVendorIds?.includes(option.value))
  }, [allVendorsSelected, options, selectedVendorIds])
  const {
    rows,
    open,
    getDropdownHandleProps,
    getMenuProps,
    getItemProps,
    getSearchInputProps,
    containerRef,
    optimisticSelectedValues,
    clearSearch,
  } = useDropdownLogic<Vendor | undefined>({
    groups: [{ id: 'vendors', options }],
    selectedValues,
    onSelect: (value) => {
      if (value.value === ALL_VENDORS_VALUE) {
        onSelect(selectedVendorIds === null ? [] : null)
        return
      }

      if (selectedVendorIds === null) {
        onSelect(
          vendors
            .map((vendor) => vendor.id)
            .filter((vendorId) => vendorId !== value.value),
        )
        return
      }

      const nextVendorIds = selectedVendorIds.includes(value.value)
        ? selectedVendorIds.filter((vendorId) => vendorId !== value.value)
        : [...selectedVendorIds, value.value].sort()
      onSelect(
        allVendorsLoaded && nextVendorIds.length === vendors.length
          ? null
          : nextVendorIds,
      )
    },
    searchable: true,
    noneLabel: '',
    multi: true,
  })

  useEffect(() => {
    if (previousOpenRef.current && !open) clearSearch()
    previousOpenRef.current = open
  }, [clearSearch, open])

  const {
    ref: handleRef,
    onKeyDown: onHandleKeyDown,
    toggleButtonProps,
  } = getDropdownHandleProps<HTMLButtonElement>()

  const { width: _width, ...menuProps } = getMenuProps()
  const activeLabel = (() => {
    if (allVendorsSelected) return translate('All Vendors')
    if (selectedVendorIds.length === 0) return translate('No Vendors')
    if (selectedVendorIds.length === 1) {
      return (
        vendors.find((vendor) => vendor.id === selectedVendorIds[0])?.name ??
        selectedVendorIds[0]
      )
    }
    return translate('${count} Vendors', { count: selectedVendorIds.length })
  })()
  const optimisticSelectedIds = new Set(
    optimisticSelectedValues.map((value) => value.value),
  )
  const allVendorsIndeterminate =
    !allVendorsSelected &&
    selectedVendorIds !== null &&
    selectedVendorIds.length > 0 &&
    selectedVendorIds.length < vendors.length

  return (
    <div
      ref={containerRef}
      className="payment-history-vendor-dropdown"
      css={style}
    >
      <Button
        {...toggleButtonProps}
        ref={handleRef}
        className="payment-history-vendor-handle"
        data-testid="payment-history-vendor-filter"
        dropShadow={false}
        tabIndex={0}
        type="button"
        variant="toolbar"
        onKeyDown={onHandleKeyDown}
      >
        <SvgSidenavPersonalInfoInactive
          className="vendor-icon"
          height={16}
          stroke="currentColor"
          width={16}
        />
        <span className="vendor-label">{activeLabel}</span>
        <SvgChevronUp
          className={cn('chevron', { open })}
          fill="currentColor"
          height={16}
          width={16}
        />
      </Button>
      <DropdownMenu
        {...menuProps}
        dropdownOptionsCount={6}
        menuTestId="payment-history-vendor-filter"
        open={open}
        searchable
        searchProps={{
          ...getSearchInputProps(),
          placeholder: translate('Search vendors...'),
        }}
        variant="small"
        width={280}
      >
        {rows.map((row, index) => {
          switch (row.kind) {
            case 'heading':
              return <li key={`heading-${index}`}>{row.heading}</li>
            case 'loading':
              return <li key="loading">{translate('Loading...')}</li>
            case 'empty':
              return (
                <li className="payment-history-vendor-empty" key="empty">
                  {translate('No matching results')}
                </li>
              )
            case 'option': {
              const option = row.option
              return (
                <DropdownOption
                  key={`${option.value}-${index}`}
                  {...option}
                  {...getItemProps({ item: row, index })}
                  indeterminate={
                    option.value === ALL_VENDORS_VALUE &&
                    allVendorsIndeterminate
                  }
                  multi
                  selected={optimisticSelectedIds.has(option.value)}
                  variant="small"
                />
              )
            }
          }
        })}
      </DropdownMenu>
    </div>
  )
}

export default PaymentHistoryVendorDropdown
